import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronDown, Check, ShoppingBag, Receipt, UserCheck, Crown, Wallet, Eye, Sparkles, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function CustomDropdown({ value, options, onChange, placeholder = "Select", className = "" }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => String(o.value) === String(value));

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-white/20 bg-black/70 text-white text-sm outline-none focus:border-hgold hover:border-hgold/70 transition-all ${className}`}
      >
        <span className={`truncate ${selectedOption ? "text-white font-medium" : "text-white/40"}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown size={16} className={`transform transition-transform duration-300 text-amber-400 shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -5 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 mt-2 rounded-2xl border-2 border-amber-400/80 bg-[#0c0c0c]/98 backdrop-blur-xl shadow-2xl shadow-yellow-950/50 z-50 p-2"
          >
            <div className="max-h-56 overflow-y-auto pr-1.5 space-y-1">
              {options.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={`w-full text-left px-3.5 py-2.5 text-xs font-bold tracking-wide flex items-center justify-between rounded-xl transition-colors ${
                      isSelected
                        ? "bg-amber-500/25 text-amber-300 border-l-4 border-amber-400"
                        : "text-white/80 hover:bg-white/10 hover:text-amber-300"
                    }`}
                  >
                    <span className="truncate pr-2">{opt.label}</span>
                    {isSelected && <Check size={15} className="text-amber-400 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AdminPOS() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  // VIP Customer Lookup States
  const [customerInfo, setCustomerInfo] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [showVipModal, setShowVipModal] = useState(false);

  const [f, setF] = useState({
    branch_id: user?.branch_id || "",
    therapist_id: "",
    service_id: "",
    customer_name: "",
    customer_phone: "",
    amount: 0,
    discount: 0,
    payment_mode: "cash",
    booking_type: "spa",
    notes: "",
  });

  useEffect(() => {
    Promise.all([
      api.get("/branches"),
      api.get("/services"),
    ]).then(([bRes, sRes]) => {
      setBranches(bRes.data);
      setServices(sRes.data);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const url = f.branch_id ? `/therapists?branch_id=${f.branch_id}` : "/therapists";
    api.get(url).then((r) => setTherapists(r.data)).catch(() => {});
  }, [f.branch_id]);

  // Real-time Customer Phone Lookup Effect
  useEffect(() => {
    const clean = (f.customer_phone || "").trim();
    if (clean.length >= 8) {
      setLookingUp(true);
      const timer = setTimeout(() => {
        api.get(`/admin/customers/lookup?phone=${clean}`)
          .then((res) => {
            if (res.data && res.data.found) {
              const cust = res.data.customer;
              setCustomerInfo(cust);
              if (cust.name && (!f.customer_name || f.customer_name === "—")) {
                setF((prev) => ({ ...prev, customer_name: cust.name }));
              }
              if (cust.wallet_balance > 0) {
                toast.success(`VIP Customer Verified! Wallet: ${inr(cust.wallet_balance)} (${cust.membership_name})`);
              }
            } else {
              setCustomerInfo(null);
            }
          })
          .catch(() => setCustomerInfo(null))
          .finally(() => setLookingUp(false));
      }, 400);
      return () => clearTimeout(timer);
    } else {
      setCustomerInfo(null);
    }
  }, [f.customer_phone, f.customer_name]);

  const selectSvc = (id) => {
    const s = services.find((x) => x.id === id);
    setF({ ...f, service_id: id, amount: s?.price || 0 });
  };

  const net = Math.max((Number(f.amount) || 0) - (Number(f.discount) || 0), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...f, amount: Number(f.amount), discount: Number(f.discount) };
      if (!payload.service_id) delete payload.service_id;
      await api.post("/admin/pos/walkin", payload);
      toast.success("Walk-in completed successfully! Receipt generated: " + new Date().toLocaleTimeString());
      setF({ ...f, customer_name: "", customer_phone: "", amount: 0, discount: 0, service_id: "", notes: "", payment_mode: "cash" });
      setCustomerInfo(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record walk-in");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-emerald-400 font-bold uppercase tracking-widest animate-pulse">Loading POS System...</div>
      </div>
    );
  }

  const branchOptions = branches.map((b) => ({ label: b.name, value: b.id }));
  const therapistOptions = therapists.map((t) => ({ label: t.display_name || t.name, value: t.id }));
  const serviceOptions = [{ label: "— Select Service —", value: "" }, ...services.map((s) => ({ label: `${s.name} · ${inr(s.price)}`, value: s.id }))];
  const paymentModeOptions = [
    { label: "Cash Payment", value: "cash" },
    { label: "UPI Online", value: "upi" },
    { label: "Credit/Debit Card", value: "card" },
    ...(customerInfo ? [{ label: `💳 Hammam Wallet (${inr(customerInfo.wallet_balance)} Available)`, value: "wallet" }] : []),
  ];

  return (
    <div data-testid="admin-pos" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border-b-2 border-white/10 pb-6"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
          <ShoppingBag size={16} /> RECEPTION DESK
        </div>
        <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
          Point of Sale · Walk-in
        </h1>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Form Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-2 rounded-2xl border border-hgold/70 p-6 lg:p-8 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 space-y-6"
        >
          {/* Service Location / Type Toggle */}
          <div>
            <label className="text-sm font-semibold text-white/90 block mb-2">Service Location / Type</label>
            <div className="grid grid-cols-2 gap-3 p-1.5 rounded-2xl bg-black border border-white/20">
              <button
                type="button"
                onClick={() => setF({ ...f, booking_type: "spa" })}
                className={`py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider font-extrabold transition-all flex items-center justify-center gap-2 ${
                  f.booking_type !== "doorstep"
                    ? "bg-gradient-to-r from-amber-400 via-hgold to-yellow-400 text-black shadow-lg shadow-amber-950/50"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                🏢 In-Spa Session
              </button>
              <button
                type="button"
                onClick={() => setF({ ...f, booking_type: "doorstep" })}
                className={`py-2.5 px-4 rounded-xl text-xs uppercase tracking-wider font-extrabold transition-all flex items-center justify-center gap-2 ${
                  f.booking_type === "doorstep"
                    ? "bg-gradient-to-r from-emerald-400 to-teal-400 text-black shadow-lg shadow-emerald-950/50"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                🚚 Doorstep Service
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {user?.role === "master_admin" && (
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Select Branch</label>
                <CustomDropdown
                  value={f.branch_id}
                  options={branchOptions}
                  onChange={(val) => setF({ ...f, branch_id: val })}
                  placeholder="Select branch"
                />
              </div>
            )}
            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Select Therapist</label>
              <CustomDropdown
                value={f.therapist_id}
                options={therapistOptions}
                onChange={(val) => setF({ ...f, therapist_id: val })}
                placeholder="Select therapist"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-white/90 block mb-1.5">Select Service</label>
            <CustomDropdown
              value={f.service_id}
              options={serviceOptions}
              onChange={(val) => selectSvc(val)}
              placeholder="Select service"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Customer Name</label>
              <input
                placeholder="Enter customer full name"
                value={f.customer_name}
                onChange={(e) => setF({ ...f, customer_name: e.target.value })}
                data-testid="pos-customer-name"
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
              />
            </div>
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-sm font-semibold text-white/90 block">Customer Phone</label>
                {lookingUp && (
                  <span className="text-[10px] text-amber-400 font-extrabold flex items-center gap-1 animate-pulse">
                    <Sparkles size={11} /> Searching VIP...
                  </span>
                )}
              </div>
              <input
                placeholder="Enter 10-digit phone number"
                value={f.customer_phone}
                onChange={(e) => setF({ ...f, customer_phone: e.target.value })}
                data-testid="pos-customer-phone"
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
              />
            </div>
          </div>

          {/* VIP Customer Info Badge & Dossier Trigger */}
          {customerInfo && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-4 rounded-xl border border-amber-400/80 bg-gradient-to-r from-amber-950/70 via-black to-emerald-950/70 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-lg shadow-yellow-950/30"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/50 flex items-center gap-1">
                    <Crown size={12} className="text-amber-400" /> {customerInfo.membership_name}
                  </span>
                  <span className="text-sm text-white font-bold">{customerInfo.name}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-white/80">
                  <span className="text-emerald-400 font-extrabold flex items-center gap-1">
                    <Wallet size={13} /> Wallet Balance: {inr(customerInfo.wallet_balance)}
                  </span>
                  {/* <span className="text-white/40">|</span>
                  <span className="text-amber-200/90 font-medium">Visits: {customerInfo.total_visits} Sessions</span> */}
                </div>
              </div>

              <div className="flex flex-col items-stretch gap-2 w-full md:w-auto shrink-0">
                {customerInfo.wallet_balance > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setF({ ...f, payment_mode: "wallet" });
                      toast.success("Selected Hammam Wallet as Payment Mode!");
                    }}
                    className="gold-btn py-2 px-4 rounded-xl text-xs font-extrabold uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-md"
                  >
                    <Wallet size={13} /> Pay via Wallet
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowVipModal(true)}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/20 py-1.5 px-4 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-colors"
                >
                  <Eye size={13} /> View Details
                </button>
              </div>
            </motion.div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Amount (₹)</label>
              <input
                type="number"
                value={f.amount}
                onChange={(e) => setF({ ...f, amount: e.target.value })}
                data-testid="pos-amount"
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-bold"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Discount (₹)</label>
              <input
                type="number"
                value={f.discount}
                onChange={(e) => setF({ ...f, discount: e.target.value })}
                data-testid="pos-discount"
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all font-bold text-amber-400"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Payment Mode</label>
              <CustomDropdown
                value={f.payment_mode}
                options={paymentModeOptions}
                onChange={(val) => setF({ ...f, payment_mode: val })}
                placeholder="Payment mode"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-white/90 block mb-1.5">Special Notes / Preferences</label>
            <textarea
              rows="2"
              placeholder="Enter optional notes..."
              value={f.notes}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
              className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all resize-none"
            />
          </div>
        </motion.div>

        {/* Right Receipt Preview Box */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="rounded-2xl border border-amber-400/80 p-6 lg:p-8 bg-gradient-to-b from-amber-950/40 via-black/95 to-hpaper shadow-xl shadow-yellow-950/20 flex flex-col justify-between"
        >
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold pb-4 border-b border-white/10">
              <Receipt size={18} /> RECEIPT PREVIEW
            </div>

            <div className="space-y-3.5 text-sm text-white/90 mt-4">
              <Row l="Customer Name" v={f.customer_name || "—"} />
              {customerInfo && <Row l="VIP Membership" v={customerInfo.membership_name} />}
              <Row l="Service Type" v={f.booking_type === "doorstep" ? "🚚 DOORSTEP SERVICE" : "🏢 IN-SPA SESSION"} />
              <Row l="Gross Amount" v={inr(f.amount)} />
              <Row l="Discount" v={inr(f.discount)} />
              
              <div className="border-t border-b border-white/15 py-4 my-2 flex justify-between items-baseline">
                <span className="text-xs uppercase tracking-widest text-amber-400 font-extrabold">Net Payable</span>
                <span className="font-heading text-3xl font-extrabold text-amber-300">{inr(net)}</span>
              </div>

              <Row l="Payment Mode" v={f.payment_mode === "wallet" ? "💳 HAMMAM WALLET" : f.payment_mode?.toUpperCase()} />

              {/* Wallet Deduction Breakdown Card */}
              {f.payment_mode === "wallet" && customerInfo && (
                <div className="p-3.5 rounded-xl border border-emerald-400/60 bg-emerald-950/40 text-xs space-y-2 mt-2">
                  <div className="flex justify-between text-white/70 font-medium">
                    <span>Wallet Balance:</span>
                    <span className="text-emerald-300 font-bold">{inr(customerInfo.wallet_balance)}</span>
                  </div>
                  <div className="flex justify-between text-white/70 font-medium">
                    <span>Net Deducted:</span>
                    <span className="text-amber-300 font-bold">-{inr(net)}</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between font-bold">
                    <span className="text-white">Remaining Balance:</span>
                    <span className={customerInfo.wallet_balance >= net ? "text-emerald-400 font-extrabold" : "text-red-400 font-extrabold"}>
                      {inr(customerInfo.wallet_balance - net)}
                    </span>
                  </div>
                  {customerInfo.wallet_balance < net && (
                    <div className="text-[10px] text-red-300 font-bold mt-1">
                      ⚠️ Insufficient wallet balance for full payment.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={submit}
            disabled={busy || !f.customer_name || !f.therapist_id || net <= 0 || (f.payment_mode === "wallet" && customerInfo && customerInfo.wallet_balance < net)}
            data-testid="pos-submit"
            className="gold-btn w-full justify-center py-4 rounded-xl font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40 flex items-center gap-2 disabled:opacity-50 mt-6"
          >
            <UserCheck size={16} />
            {busy ? "Recording Walk-In..." : "Complete Sale"}
          </button>
        </motion.div>
      </div>

      {/* Detailed VIP Dossier Modal */}
      {showVipModal && customerInfo && (
        <Dialog open={showVipModal} onOpenChange={setShowVipModal}>
          <DialogContent className="bg-[#0c0c0c] border-2 border-amber-400/90 text-white max-w-md mx-auto rounded-2xl p-6 shadow-2xl">
            <DialogHeader className="border-b border-white/10 pb-4">
              <div className="text-[11px] uppercase font-extrabold tracking-widest text-emerald-400 mb-1 flex items-center gap-1.5">
                <ShieldCheck size={15} /> Verified Hammam VIP Profile
              </div>
              <DialogTitle className="text-2xl font-heading text-amber-300 font-bold">
                {customerInfo.name}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-3 text-sm">
              <div className="p-4 rounded-xl border border-white/10 bg-white/5 space-y-2.5">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-white/60">Phone:</span>
                  <span className="text-white font-bold">{customerInfo.phone}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-white/60">Email:</span>
                  <span className="text-white font-bold">{customerInfo.email || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-white/60">Active Membership:</span>
                  <span className="text-amber-300 font-bold flex items-center gap-1">
                    <Crown size={13} /> {customerInfo.membership_name}
                  </span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-white/60">Wallet Balance:</span>
                  <span className="text-emerald-400 font-extrabold text-base">{inr(customerInfo.wallet_balance)}</span>
                </div>
                {/* <div className="flex justify-between items-center text-xs">
                  <span className="text-white/60">Total Spa Visits:</span>
                  <span className="text-amber-200 font-bold">{customerInfo.total_visits} Completed Sessions</span>
                </div> */}
              </div>

              {customerInfo.wallet_balance > 0 ? (
                <button
                  type="button"
                  onClick={() => {
                    setF({ ...f, payment_mode: "wallet" });
                    toast.success("Payment mode set to Hammam Wallet!");
                    setShowVipModal(false);
                  }}
                  className="gold-btn w-full justify-center py-3.5 text-xs uppercase font-extrabold rounded-xl shadow-lg shadow-yellow-950/30 flex items-center gap-2"
                >
                  <Wallet size={15} /> Select Hammam Wallet ({inr(customerInfo.wallet_balance)})
                </button>
              ) : (
                <div className="text-xs text-white/50 text-center py-2 font-medium">
                  Wallet Balance is ₹0. Customer can top up via Membership Plans.
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-xs font-medium text-white/60 uppercase tracking-wider">{l}</span>
      <span className="font-bold text-white text-sm">{v}</span>
    </div>
  );
}
